import { Link } from 'wouter';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { Calendar, NotebookPen } from 'lucide-react';
import { StatusBadge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { getDateFormats, getDateLocale } from '@/utils/dateLocale';
import type { BookingStatus } from '@/lib/status-colors';
import type { CustomerBooking } from '@/types/customer';
import { BookingNoteCell } from './BookingNoteCell';
import { noteCellState } from './noteCellState';

/**
 * The therapist's version of the customer's booking history: each booking
 * with its session note. A table from `sm` up, a list of rows on phones.
 * The date opens the booking; the note cell opens or starts the note.
 */
export function CustomerBookingsWithNotes({ bookings }: { bookings: CustomerBooking[] }) {
  const { t } = useTranslation(['customers', 'bookings', 'sessions']);

  if (bookings.length === 0) {
    return (
      <div className="px-4 py-12 text-center">
        <Calendar className="mx-auto mb-3 h-8 w-8 text-soft" aria-hidden="true" />
        <p className="text-soft">{t('customers:detail.noBookings')}</p>
      </div>
    );
  }

  const rows = bookings.map((booking) => {
    const id = booking.publicId ?? booking.id;
    const start = booking.startTime ? parseISO(booking.startTime) : null;
    return {
      booking,
      id,
      date: start ? format(start, getDateFormats().medium, { locale: getDateLocale() }) : '—',
      time: start ? format(start, 'HH:mm') : '',
      status: booking.status as BookingStatus,
      statusLabel: t(`bookings:status.${booking.status === 'no_show' ? 'noShow' : booking.status}`),
      hasNoteLine: noteCellState(booking.sessionNote, booking.startTime, booking.status).kind !== 'none',
    };
  });

  return (
    <>
      <table className="hidden w-full text-body-sm sm:table">
        <thead>
          <tr className="border-b border-line bg-surface-2/60">
            <th scope="col" className="whitespace-nowrap px-4 py-3 text-left font-semibold text-soft">{t('customers:bookingTable.date')}</th>
            <th scope="col" className="whitespace-nowrap px-4 py-3 text-left font-semibold text-soft">{t('customers:bookingTable.service')}</th>
            <th scope="col" className="whitespace-nowrap px-4 py-3 text-left font-semibold text-soft">{t('customers:bookingTable.status')}</th>
            <th scope="col" className="whitespace-nowrap px-4 py-3 text-left font-semibold text-soft">{t('sessions:customerTab.colNote')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.booking.id} className="border-b border-line last:border-0">
              <td className="whitespace-nowrap px-4 py-3">
                <Link href={`/admin/bookings/${row.id}`} className="font-semibold text-ink hover:text-brand-700">
                  {row.date}
                </Link>
                <p className="m-0 text-body-xs text-soft">{row.time}</p>
              </td>
              <td className="px-4 py-3">
                <p className="m-0 text-ink">{row.booking.serviceName ?? '—'}</p>
                {row.booking.providerName && <p className="m-0 text-body-xs text-soft">{row.booking.providerName}</p>}
              </td>
              <td className="px-4 py-3">
                <StatusBadge status={row.status}>{row.statusLabel}</StatusBadge>
              </td>
              <td className="px-4 py-3">
                <BookingNoteCell
                  bookingId={row.id}
                  summary={row.booking.sessionNote}
                  startTime={row.booking.startTime}
                  status={row.booking.status}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Phones: date and booking status, the service, then a labelled note line
          so the note's status is never mistaken for the booking's. */}
      <ul className="m-0 list-none p-0 sm:hidden">
        {rows.map((row, i) => (
          <li key={row.booking.id} className={cn('px-4 py-3.5', i > 0 && 'border-t border-line')}>
            <div className="flex items-start justify-between gap-3">
              <Link href={`/admin/bookings/${row.id}`} className="min-w-0 text-ink hover:text-brand-700">
                <span className="text-sm font-semibold">{row.date}</span>
                <span className="text-sm text-soft"> · {row.time}</span>
              </Link>
              <StatusBadge status={row.status}>{row.statusLabel}</StatusBadge>
            </div>
            <p className="m-0 mt-0.5 truncate text-[13px] text-soft">
              {[row.booking.serviceName, row.booking.providerName].filter(Boolean).join(' · ') || '—'}
            </p>
            {row.hasNoteLine && (
              <div className="mt-3 flex items-center gap-2 border-t border-dashed border-line pt-3">
                <NotebookPen className="h-4 w-4 shrink-0 text-soft" aria-hidden="true" />
                <span className="text-xs font-semibold text-soft">{t('sessions:customerTab.colNote')}</span>
                <span className="flex-1" />
                <BookingNoteCell
                  bookingId={row.id}
                  summary={row.booking.sessionNote}
                  startTime={row.booking.startTime}
                  status={row.booking.status}
                  large
                />
              </div>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}
